import { parseISO } from 'date-fns';
import { TransactionRegistrationProposalEntryResponse } from '~/api/bookkeeping/bookkeeping-bank-transactions';
import { Document, DocumentDirection, DocumentType } from '~/types/document';
import {
  BookkeepingAdjustmentSourceType,
  BookkeepingEntry,
  CreateBookkeepingEntry,
  TransactionRegistrationProposalEntry,
} from '~/types/general-bookkeeping';
import { type AdjustmentDescriptionFn, buildAdjustmentPayload } from '~/utils/bookkeeping/adjustments';
import { absMoney, isLessThanZero, isMoreThanZero, negateMoney } from '~/utils/money';
import {
  BookkeepingEntryFormInput,
  BookkeepingEntryFormOutput,
} from '~/views/bookkeeping/transactions/BookkeepingTransactionSchema';

export const isDare = (entry: BookkeepingEntry | TransactionRegistrationProposalEntryResponse) => {
  return isLessThanZero(entry.amount);
};

export const isAvere = (entry: BookkeepingEntry | TransactionRegistrationProposalEntryResponse) => {
  return isMoreThanZero(entry.amount);
};

export const isPaidInDare = (document: Pick<Document, 'direction' | 'documentType'>): boolean => {
  const isCreditNote = document.documentType === DocumentType.CreditNote;
  const isIssued = document.direction === DocumentDirection.issued;
  return isCreditNote ? isIssued : !isIssued;
};

export const bookkeepingEntryToFormEntry = (entry: BookkeepingEntry): BookkeepingEntryFormOutput => {
  return {
    id: entry.id,
    bookkeepingAccountId: entry.bookkeepingAccount.id,
    counterpart: entry.counterpart ?? null,
    description: entry.description ?? '',
    dare: isDare(entry) ? absMoney(entry.amount) : null,
    avere: isAvere(entry) ? absMoney(entry.amount) : null,
    flowId: entry.flow?.id ?? null,
    documentId: entry.document?.id ?? null,
    documentNumber: entry.document?.number ?? null,
    documentType: entry.document?.documentType ?? null,
    source: entry.source,
    competencePeriodStart: entry.competencePeriodStart ? parseISO(entry.competencePeriodStart) : null,
    competencePeriodEnd: entry.competencePeriodEnd ? parseISO(entry.competencePeriodEnd) : null,
    adjustmentAccountId: entry.bookkeepingAdjustment?.bookkeepingAccount.id ?? null,
  };
};

export const formEntriesToCreateBookkeepingEntries = (
  entries: BookkeepingEntryFormOutput[],
  registrationDate: Date,
  getDescription: AdjustmentDescriptionFn,
): Omit<CreateBookkeepingEntry, 'lid'>[] => {
  return entries.map((entry) => {
    const { counterpart, dare, avere, adjustmentAccountId, ...rest } = entry;
    return {
      ...rest,
      counterpartId: counterpart?.id ?? null,
      amount: dare ? negateMoney(dare) : avere!,
      bookkeepingAdjustment: buildAdjustmentPayload(
        adjustmentAccountId,
        entry.bookkeepingAccountId,
        registrationDate,
        entry.competencePeriodStart,
        entry.competencePeriodEnd,
        !!dare,
        dare ?? avere!,
        BookkeepingAdjustmentSourceType.BOOKKEEPING_ENTRY,
        getDescription,
      ),
    };
  });
};

export const proposalEntriesToFormEntries = (
  entries: TransactionRegistrationProposalEntry[],
): BookkeepingEntryFormInput[] => {
  return entries.map((entry) => ({
    id: null,
    bookkeepingAccountId: entry.bookkeepingAccount?.id || '',
    counterpart: entry.counterpart || null,
    description: '',
    dare: isDare(entry) ? absMoney(entry.amount) : null,
    avere: isAvere(entry) ? absMoney(entry.amount) : null,
    flowId: entry.flow?.id || null,
    documentId: entry.document?.id || null,
    documentNumber: entry.document?.number || null,
    documentType: entry.document?.documentType || null,
    source: entry.source,
    competencePeriodStart: null,
    competencePeriodEnd: null,
  }));
};
